/**
 * InventoryTable (PROD-1278 feedback #4) — the Inventory page's rows as the
 * app's standard table: one row per package/deliverable the kitchen sent for
 * the mealtime, with what was delivered, what went out, what is left, what is
 * available, and a status badge.
 *
 * "Available" (2026-09-14 team review: "available is not reserved") is what
 * the line can hand to whoever comes: what is left that no student's order
 * still claims, and not sold out. Extras beyond the orders count; a short
 * delivery reads 0. The worker's own count replaces the derived number.
 *
 * Two states, both rendered by this one component so the columns never move:
 *   read — numbers and a status badge (Sold out, None left, Counted by hand;
 *          a planned or edited Delivered number is not flagged, since
 *          cafeteria mode no longer confirms deliveries);
 *   edit — the Delivered column takes the worker's own count of what arrived
 *          (blank = trust the shipment numbers; the production record's
 *          Confirm deliveries step reads it back as a prefill), the Left
 *          column takes the worker's on-hand count (blank = trust the delivery
 *          numbers), the Available column takes how many are available to
 *          sell (blank = what is left minus the students' orders still to be
 *          served; the school's pre-ordered pool when nothing shipped), and
 *          the Status column becomes a Sold out switch.
 * `loading` swaps the body for skeleton rows — on the cold load AND while a
 * mealtime switch is in flight, so another mealtime's numbers are never
 * shown under this tab.
 *
 * Presentational: the page owns the draft, the mode and the save.
 */

import { Badge } from '@ordo/ui/badge'
import { Skeleton } from '@ordo/ui/skeleton'
import { Switch } from '@ordo/ui/switch'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@ordo/ui/table'

import type { InventoryRowWire } from '@/api/cafeteria-inventory'
import { CountInput } from '@/components/cafeteria/count-input'
import {
  formatInventoryCount,
  INVENTORY_STATUS_LABEL,
  inventoryRowStatus,
  inventoryStatusBadgeVariant,
} from '@/lib/cafeteria/inventory'
import { type InventoryEditDraft, type InventoryEditDraftRow, parseOnHand } from '@/lib/cafeteria/inventory-edit'

export const INVENTORY_TABLE_SKELETON_ROWS = 6

export interface InventoryTableEdit {
  draft: InventoryEditDraft
  onChange: (itemKey: string, next: InventoryEditDraftRow) => void
  /** A save in flight: the boxes stay visible but locked. */
  disabled: boolean
}

export interface InventoryTableProps {
  rows: ReadonlyArray<InventoryRowWire>
  /** Skeleton rows instead of data — the cold load, or a mealtime switch still loading. */
  loading: boolean
  /** Null = the read state. */
  edit: InventoryTableEdit | null
}

const NUMERIC_HEAD = 'text-right'
const NUMERIC_CELL = 'text-right tabular-nums'
const COLUMN_COUNT = 6

export function InventoryTable({ rows, loading, edit }: InventoryTableProps) {
  const editing = edit !== null
  return (
    <TableContainer>
      {/* `min-w-0` lets the table fit its container (item names wrap) instead of the primitive's max-content scroll. */}
      <Table className="min-w-0" aria-busy={loading || undefined}>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className={NUMERIC_HEAD}>Delivered</TableHead>
            <TableHead className={NUMERIC_HEAD}>Served</TableHead>
            <TableHead className={NUMERIC_HEAD}>{editing ? 'On hand now' : 'Left'}</TableHead>
            <TableHead className={NUMERIC_HEAD}>Available</TableHead>
            <TableHead>{editing ? 'Sold out' : 'Status'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <SkeletonRows />
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={COLUMN_COUNT}
                className="whitespace-normal py-8 text-center text-sm text-muted-foreground">
                Nothing on hand for this mealtime yet. Inventory appears once the kitchen ships or the school orders for
                it.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => <InventoryTableRow key={row.itemKey} row={row} edit={edit} />)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function InventoryTableRow({ row, edit }: { row: InventoryRowWire; edit: InventoryTableEdit | null }) {
  const status = inventoryRowStatus(row)
  const draftRow: InventoryEditDraftRow = edit?.draft[row.itemKey] ?? {
    onHand: '',
    forSale: '',
    delivered: '',
    soldOut: row.soldOut,
  }
  const invalid = edit !== null && parseOnHand(draftRow.onHand) === undefined
  const forSaleInvalid = edit !== null && parseOnHand(draftRow.forSale ?? '') === undefined
  const deliveredInvalid = edit !== null && parseOnHand(draftRow.delivered ?? '') === undefined
  return (
    <TableRow>
      <TableCell className="whitespace-normal font-medium">{row.label}</TableCell>
      <TableCell className={NUMERIC_CELL}>
        {edit ? (
          <CountInput
            aria-label={`Delivered: ${row.label}`}
            aria-invalid={deliveredInvalid || undefined}
            placeholder={row.shipmentDelivered === null ? '' : String(row.shipmentDelivered)}
            value={draftRow.delivered ?? ''}
            disabled={edit.disabled}
            onValueChange={(next) => edit.onChange(row.itemKey, { ...draftRow, delivered: next })}
            className="ml-auto w-24 text-right"
          />
        ) : (
          <Count value={row.delivered} />
        )}
      </TableCell>
      <TableCell className={NUMERIC_CELL}>{row.served}</TableCell>
      <TableCell className={NUMERIC_CELL}>
        {edit ? (
          <CountInput
            aria-label={`On hand now: ${row.label}`}
            aria-invalid={invalid || undefined}
            placeholder={row.left === null ? '' : String(row.left)}
            value={draftRow.onHand}
            disabled={edit.disabled}
            onValueChange={(next) => edit.onChange(row.itemKey, { ...draftRow, onHand: next })}
            className="ml-auto w-24 text-right"
          />
        ) : (
          <Count value={row.left} />
        )}
      </TableCell>
      <TableCell className={NUMERIC_CELL}>
        {edit ? (
          <CountInput
            aria-label={`Available: ${row.label}`}
            aria-invalid={forSaleInvalid || undefined}
            placeholder={String(row.forSale)}
            value={draftRow.forSale ?? ''}
            disabled={edit.disabled}
            onValueChange={(next) => edit.onChange(row.itemKey, { ...draftRow, forSale: next })}
            className="ml-auto w-24 text-right"
          />
        ) : (
          row.forSale
        )}
      </TableCell>
      <TableCell>
        {edit ? (
          <Switch
            aria-label={`Sold out: ${row.label}`}
            checked={draftRow.soldOut}
            disabled={edit.disabled}
            onCheckedChange={(checked) => edit.onChange(row.itemKey, { ...draftRow, soldOut: checked === true })}
          />
        ) : status ? (
          <Badge variant={inventoryStatusBadgeVariant(status)}>{INVENTORY_STATUS_LABEL[status]}</Badge>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

/** A count; an unknown value reads as a muted dash, never a fake zero. */
function Count({ value }: { value: number | null }) {
  const text = formatInventoryCount(value)
  return value === null ? <span className="text-muted-foreground">{text}</span> : <>{text}</>
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: INVENTORY_TABLE_SKELETON_ROWS }, (_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-4 w-56 max-w-full" />
          </TableCell>
          {Array.from({ length: 4 }, (_, j) => (
            <TableCell key={j} className={NUMERIC_CELL}>
              <Skeleton className="ml-auto h-4 w-10" />
            </TableCell>
          ))}
          <TableCell>
            <Skeleton className="h-5 w-24 rounded-full" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}
